import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./kit";
import { CheckCircle2, ChevronDown, ChevronRight, Sparkles, Loader2, AlertTriangle, ShieldCheck, RefreshCw } from "lucide-react";

// Workable review card. A Steel cloud browser filled the application to review (never submitted);
// this shows the structured answers (editable) + the screenshot of the actual filled form, and
// approving re-fills the final answers and submits. Self-gating: renders nothing if this draft has
// no Workable review (so ApplyStatus can drop it in unconditionally).
const SRC = { profile: ["Profile", "#0025D8"], ai: ["AI", "#DB2777"] };

// Module-scoped so it keeps a stable identity across renders — a component defined inside the parent
// would be a new type every keystroke and the input would lose focus after each character.
// A multipicklist value can be an array or a comma/semicolon-joined string → normalize to an array.
function asArray(v) {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  return String(v || "").split(/[;,]/).map((s) => s.trim()).filter(Boolean);
}

// Flatten a value for display/editing. A structured value (e.g. an address dict {street,city,state,zip,
// country}) must never render as "[object Object]" — join its parts into a readable line. Arrays (multi-
// select) are left as-is; strings/numbers pass through.
function flatVal(v) {
  if (v == null) return "";
  if (Array.isArray(v)) return v;
  if (typeof v === "object") return Object.values(v).filter((x) => x != null && x !== "").join(", ");
  return v;
}
// Normalize every answer/needs_user value so the whole card (display AND what we submit) uses primitives.
function normReview(d) {
  if (!d || typeof d !== "object") return d;
  const fix = (arr) => (Array.isArray(arr) ? arr.map((a) => ({ ...a, value: flatVal(a.value) })) : arr);
  return { ...d, answers: fix(d.answers), needs_user: fix(d.needs_user) };
}

function Row({ name, label, value: rawValue, source, needsYou, options, multi, onChange }) {
  const value = flatVal(rawValue);                 // defensive: never render a raw object
  const [lab, col] = SRC[source] || SRC.ai;
  const opts = (options || []).filter((o) => o && String(o).toLowerCase() !== "select one");
  const long = String(value || "").length > 60;
  return (
    <div className="py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center justify-between gap-2 mb-1">
        <label className="text-[13px] font-medium text-slate-700">{label || name}</label>
        {needsYou
          ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#E08A00]/12 text-[#E08A00]">Needs you</span>
          : <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: col + "18", color: col }}>{lab}</span>}
      </div>
      {opts.length > 0 && multi ? (
        // multi-select question → toggle several choices (value stays an array; backend expects a list)
        <>
          <div className="flex flex-wrap gap-2">
            {opts.map((o) => {
              const arr = asArray(value);
              const sel = arr.includes(String(o));
              return (
                <button key={o} type="button"
                  onClick={() => onChange(sel ? arr.filter((x) => x !== String(o)) : [...arr, String(o)])}
                  className={`text-[13px] px-3 py-1.5 rounded-lg border transition-colors ${
                    sel ? "border-[#0025D8] bg-[#0025D8]/8 text-[#0025D8] font-semibold"
                        : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
                  {sel ? "✓ " : ""}{o}
                </button>
              );
            })}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">Select all that apply</div>
        </>
      ) : opts.length > 0 ? (
        // single-select question → real choices (radio-style pills), not a free-text box
        <div className="flex flex-wrap gap-2">
          {opts.map((o) => {
            const sel = String(value ?? "") === String(o);
            return (
              <button key={o} type="button" onClick={() => onChange(o)}
                className={`text-[13px] px-3 py-1.5 rounded-lg border transition-colors ${
                  sel ? "border-[#0025D8] bg-[#0025D8]/8 text-[#0025D8] font-semibold"
                      : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
                {o}
              </button>
            );
          })}
        </div>
      ) : long ? (
        <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full text-[13px] rounded-lg border border-slate-200 px-3 py-2 focus:border-[#0025D8] outline-none" />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={needsYou ? "Add your answer…" : ""}
          className="w-full text-[13px] rounded-lg border border-slate-200 px-3 py-2 focus:border-[#0025D8] outline-none" />
      )}
    </div>
  );
}

export default function WorkableReview({ draftId, onSubmitted }) {
  const [rev, setRev] = useState(null);      // null=loading, false=none, object=review
  const [edits, setEdits] = useState({});    // {name: value}
  const [showShot, setShowShot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState("");        // candidate-typed CAPTCHA code
  const [captchaImg, setCaptchaImg] = useState("");  // base64 JPEG of the current challenge
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [manualCaptcha, setManualCaptcha] = useState(false);  // true only AFTER auto-solve fails → ask candidate

  const load = useCallback(() => {
    api.get(`/apply/automation/review/${draftId}`)
      .then(({ data }) => setRev(normReview(data)))
      .catch(() => setRev(false));
  }, [draftId]);
  useEffect(() => { load(); }, [load]);

  // The cloud engine can flip the draft to "ready for review" a beat before it finishes writing the review
  // payload — so the initial mount fetch can come back empty. Keep re-fetching (bounded) until the review
  // is actually populated, so the card fills in on its own without a manual page refresh.
  const revReady = (r) => !!(r && typeof r === "object" &&
    ((r.answers && r.answers.length) || (r.needs_user && r.needs_user.length) || r.captcha_required));
  const revPollStart = useRef(0);
  const [pollTick, setPollTick] = useState(0);
  useEffect(() => {
    if (revReady(rev)) { revPollStart.current = 0; return; }
    if (!revPollStart.current) revPollStart.current = Date.now();
    if (Date.now() - revPollStart.current > 120000) return;   // bounded — never poll forever (cloud prep + queue)
    let alive = true;
    const timer = setTimeout(async () => {
      try { const { data } = await api.get(`/apply/automation/review/${draftId}`); if (alive && data) setRev(normReview(data)); }
      catch { /* not ready yet — keep waiting */ }
      // Re-tick so this effect re-runs and schedules the NEXT poll even when the fetch left `rev` unchanged
      // (a 404 while the review is still being written) — otherwise the poll would fire only once and stall.
      finally { if (alive) setPollTick((t) => t + 1); }
    }, 3000);
    return () => { alive = false; clearTimeout(timer); };
  }, [rev, draftId, pollTick]);

  // Fetch a fresh CAPTCHA image (also refreshes the server-side session cookies the submit reuses).
  const loadCaptcha = useCallback(() => {
    setCaptchaLoading(true);
    setCaptcha("");
    api.get(`/apply/automation/captcha/${draftId}`)
      .then(({ data }) => setCaptchaImg(data.image_b64 || ""))
      .catch(() => toast.error("Couldn't load the security code — try refreshing it."))
      .finally(() => setCaptchaLoading(false));
  }, [draftId]);

  // When the review says a CAPTCHA is required, load the first image once it's known.
  // Only load the image once we actually need the candidate to type it (auto-solve is tried first server-side).
  useEffect(() => { if (rev && rev.captcha_required && manualCaptcha && !captchaImg) loadCaptcha(); }, [rev, manualCaptcha, captchaImg, loadCaptcha]);

  if (rev === null || rev === false) return null;   // loading or not a Workable review → nothing

  const valueOf = (a) => (a.name in edits ? edits[a.name] : a.value);
  const setVal = (name, v) => setEdits((e) => ({ ...e, [name]: v }));

  const approve = async () => {
    // The security code is solved automatically on the first try; only require manual entry if that failed.
    if (rev.captcha_required && manualCaptcha && !captcha.trim()) {
      toast.error("Enter the security code shown before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      // final = answered (with edits) + any needs-you the candidate filled
      const hasVal = (v) => v != null && (Array.isArray(v) ? v.length > 0 : String(v) !== "");
      const merged = {};
      for (const a of rev.answers || []) merged[a.name] = valueOf(a);
      for (const n of rev.needs_user || []) if (hasVal(edits[n.name])) merged[n.name] = edits[n.name];
      const answers = Object.entries(merged).map(([name, value]) => ({ name, value }));
      const { data } = await api.post(`/apply/automation/submit/${draftId}`,
        { answers, captcha_answer: captcha.trim() });
      if (data.submitted) { toast.success("Application submitted ✓"); setRev(false); }  // hide → reload shows receipt
      else if (data.needs_captcha) {
        // Auto-solve couldn't crack it — fall back to manual entry: reveal the field + load a fresh image.
        setManualCaptcha(true);
        toast.error(data.error || "Please enter the security code shown to continue.");
        loadCaptcha();
      }
      else if (data.needs_user) {
        // The employer's form had screening questions we couldn't answer for you — they've been added
        // below. Re-fetch the review so they render, then you fill them in and Approve & submit again.
        toast.message("A few screening questions need your answer — please fill them in below.");
        load();
      }
      else if (!data.live_submit_enabled)
        toast.message("Filled & approved — live submit is off (nothing was actually sent).");
      else toast.error(data.error || "Submit didn't confirm.");
      onSubmitted && onSubmitted(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="rounded-2xl border border-[#7C3AED]/25 bg-white overflow-hidden mb-5">
      <div className="px-4 py-3 bg-[#7C3AED]/6 border-b border-[#7C3AED]/15 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-[#7C3AED]" />
        <div className="text-[13px] font-semibold text-slate-800">
          Review your application — {rev.company}{rev.title ? ` · ${rev.title}` : ""}
        </div>
      </div>

      <div className="px-4 py-2">
        {(rev.answers || []).map((a) => (
          <Row key={a.name} name={a.name} label={a.label} value={valueOf(a)} source={a.source}
            options={a.options} multi={a.multi} onChange={(v) => setVal(a.name, v)} />
        ))}
        {(rev.needs_user || []).map((n) => (
          <Row key={n.name} name={n.name} label={n.label}
            value={edits[n.name] != null ? edits[n.name] : (n.value || "")} needsYou
            options={n.options} multi={n.multi} onChange={(v) => setVal(n.name, v)} />
        ))}
        {(rev.answers || []).length === 0 && (rev.needs_user || []).length === 0 && (
          <div className="py-3 text-[13px] text-slate-500">No fields were captured — open the screenshot to check the form.</div>
        )}
      </div>

      {rev.screenshot_b64 && (
        <div className="px-4 pb-2">
          <button onClick={() => setShowShot((s) => !s)}
            className="flex items-center gap-1 text-[12px] font-medium text-[#0025D8] py-1">
            {showShot ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            See the actual filled form
          </button>
          {showShot && (
            <div className="mt-1 max-h-[420px] overflow-auto rounded-lg border border-slate-200">
              <img alt="Filled application" src={`data:image/png;base64,${rev.screenshot_b64}`} className="w-full" />
            </div>
          )}
        </div>
      )}

      {(rev.needs_user || []).length > 0 && (
        <div className="mx-4 mb-2 flex items-start gap-2 text-[12px] text-[#E08A00] bg-[#E08A00]/8 rounded-lg px-3 py-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{rev.needs_user.length} question{rev.needs_user.length > 1 ? "s" : ""} the AI wouldn't guess — add your answer{rev.needs_user.length > 1 ? "s" : ""} above before submitting.</span>
        </div>
      )}

      {rev.captcha_required && manualCaptcha && (
        <div className="mx-4 mb-2 rounded-xl border border-[#0025D8]/20 bg-[#0025D8]/[0.04] px-3 py-3">
          <div className="flex items-center gap-1.5 mb-2">
            <ShieldCheck className="w-3.5 h-3.5 text-[#0025D8]" />
            <span className="text-[12px] font-semibold text-slate-800">Security check</span>
            <span className="text-[11px] text-slate-500">— this employer asks you to type the code shown</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-[46px] w-[150px] rounded-lg border border-slate-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
              {captchaLoading
                ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                : captchaImg
                  ? <img alt="Security code" src={`data:image/jpeg;base64,${captchaImg}`} className="max-h-[44px]" />
                  : <span className="text-[11px] text-slate-400">no image</span>}
            </div>
            <button type="button" onClick={loadCaptcha} disabled={captchaLoading} title="New code"
              className="h-[46px] w-[38px] rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-[#0025D8] hover:border-[#0025D8]/30 transition-colors shrink-0">
              <RefreshCw className={`w-3.5 h-3.5 ${captchaLoading ? "animate-spin" : ""}`} />
            </button>
            <input value={captcha} onChange={(e) => setCaptcha(e.target.value)}
              placeholder="Enter code" autoComplete="off" spellCheck={false}
              className="flex-1 h-[46px] text-[14px] tracking-wide rounded-lg border border-slate-200 px-3 focus:border-[#0025D8] outline-none" />
          </div>
        </div>
      )}

      <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-end">
        <Button variant="navy" size="sm" onClick={approve} disabled={submitting}>
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          Approve &amp; submit
        </Button>
      </div>
    </div>
  );
}
